import { useSelector } from 'react-redux';

import { Box, Typography } from '@material-ui/core';
import DescriptionIcon from '@material-ui/icons/Description';

import { Button, FormGrid, Restricted } from 'components';
import { useQuoteBindStyles } from 'modules/QuoteBind';

import { useConfigContext } from 'config';
import { ROLE_BROKER, ROLE_PRODUCER } from 'consts';
import { ENABLED_ENDORSEMENT_PRODUCTS_PROD } from 'consts/quote-bind';
import * as utils from 'utils';

import { EndorsementQuotes, Endorsements, NewEndorsement } from './Endorsement';
import { PolicyCard, PolicyCardSkeleton } from './PolicyCard';

export const RiskPolicyView = ({
  risk,
  isLoading,
  isEndorsementLoading,
  riskIsLoading,
  policy,
  isCanceled,
  isExtended,
  policyCurrency,
  hasBoundQuote,
  parties,
  effectiveEndorsements,
  pendingEndorsements,
  quotedEndorsements,
  activeEndorsements,
  latestEffectiveEndorsementDate,
  handleEndorsementRefresh,
  hasBackdateCancellation,
  hasBackdateEndorsement,
  hasEndorsementAdditionalPremium,
  hasRestrictedEndorsementForProducer,
  hasSurplusLineTax,
  handleAddEndorsement,
  handleReQuoteEndorsement,
  handlePreBind,
}) => {
  const classes = useQuoteBindStyles();
  const config = useConfigContext();
  const showPostBind = policy?.facility?.postBind || false;
  const user = useSelector((state) => state.user);
  const isProducer = utils.user.isProducer(user);

  const isEnabled = () => {
    if (isProducer && hasRestrictedEndorsementForProducer) {
      return false;
    }

    if (config.features?.ENABLE_ENDORSEMENT === true) {
      return true;
    }

    return ENABLED_ENDORSEMENT_PRODUCTS_PROD.includes(policy?.riskType);
  };

  const isMultipleEndorsementResticted = risk?.productConfig?.areMultipleEndorsementsRestricted && activeEndorsements?.length > 0;

  return (
    <>
      <Box className={classes.root} data-testid="RiskPolicy">
        <Box className={classes.header} mb={2} display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" justifyContent="start">
            <Typography variant="h3" className={classes.title}>
              {utils.string.t('app.policy')}
            </Typography>
          </Box>
          <Restricted include={[ROLE_BROKER, ROLE_PRODUCER]}>
            <Box display="flex" alignItems="center" justifyContent="end">
              {hasBoundQuote ? (
                <>
                  {showPostBind ? (
                    <Button
                      icon={DescriptionIcon}
                      size="xsmall"
                      variant="outlined"
                      color="primary"
                      text={utils.string.t('app.preBindForm')}
                      onClick={handlePreBind(policy, false)}
                      style={{ marginRight: 10 }}
                    />
                  ) : null}
                  {isEnabled() && !isMultipleEndorsementResticted ? (
                    <NewEndorsement
                      handleAddEndorsement={handleAddEndorsement}
                      policy={policy}
                      hasBackdateEndorsement={hasBackdateEndorsement}
                      latestEffectiveEndorsementDate={latestEffectiveEndorsementDate}
                    />
                  ) : null}
                </>
              ) : null}
            </Box>
          </Restricted>
        </Box>
        <Box className={classes.content}>
          <FormGrid container spacing={2}>
            {isLoading || riskIsLoading || !policy ? (
              <FormGrid item xs={12} sm={6} md={6} lg={4}>
                <PolicyCardSkeleton />
              </FormGrid>
            ) : (
              <>
                <FormGrid item xs={12} sm={6} md={6} lg={4}>
                  <PolicyCard
                    policy={policy}
                    hasBoundQuote={hasBoundQuote}
                    parties={parties}
                    handlePreBind={handlePreBind}
                    endorsements={effectiveEndorsements}
                    hasAnyEndorsements={effectiveEndorsements?.length > 0 || pendingEndorsements?.length || quotedEndorsements?.length}
                    isCanceled={isCanceled}
                    isExtended={isExtended}
                    hasSurplusLineTax={hasSurplusLineTax}
                    hasBackdateCancellation={hasBackdateCancellation}
                    isUnbindAllowed={risk?.unbindAllowed ?? true}
                  />
                </FormGrid>
                <FormGrid item xs={12} sm={6} md={6} lg={4}>
                  {effectiveEndorsements?.length > 0 ? (
                    <Endorsements
                      title={utils.string.t('app.activeEndorsement_plural')}
                      endorsements={effectiveEndorsements}
                      policyCurrency={policyCurrency}
                      riskType={policy?.riskType}
                      displayIndex
                      testid="effectiveEndorsements"
                    />
                  ) : null}
                </FormGrid>
                <FormGrid item xs={12} sm={6} md={6} lg={4}>
                  {pendingEndorsements?.length > 0 ? (
                    <Endorsements
                      title={utils.string.t('app.boundEndorsement_plural')}
                      canCancel={false}
                      endorsements={pendingEndorsements}
                      policyCurrency={policyCurrency}
                      riskType={policy?.riskType}
                      testid="pendingEndorsements"
                    />
                  ) : null}
                </FormGrid>
              </>
            )}
          </FormGrid>
        </Box>
      </Box>
      {quotedEndorsements?.length > 0 ? (
        <EndorsementQuotes
          isEndorsementLoading={isEndorsementLoading}
          risk={risk}
          title={utils.string.t('app.endorsements_quote_plural')}
          endorsements={quotedEndorsements}
          policyCurrency={policyCurrency}
          policy={policy}
          isEndorsementEnabled={isEnabled()}
          hasBackdateEndorsement={hasBackdateEndorsement}
          latestEffectiveEndorsementDate={latestEffectiveEndorsementDate}
          hasEndorsementAdditionalPremium={hasEndorsementAdditionalPremium}
          handleReQuoteEndorsement={handleReQuoteEndorsement}
          handleEndorsementRefresh={handleEndorsementRefresh}
        />
      ) : null}
    </>
  );
};
